import { Capacitor } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { compareAppVersions, isValidAppVersion } from '@tupai/types';
import { z } from 'zod';

const manifestSchema = z.object({
  schemaVersion: z.literal(1),
  appVersion: z.string().refine(isValidAppVersion).optional(),
  app: z.enum(['student', 'parent']),
  platform: z.enum(['android', 'ios']),
  compatibleNativeBuilds: z.array(z.string().min(1)).min(1),
  bundleId: z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/)
    .refine((id) => id !== 'public'),
  downloadUrl: z.url(),
  sha256: z.string().regex(/^[a-fA-F0-9]{64}$/),
  signature: z.string().min(1),
});

type App = 'student' | 'parent';
type Manifest = z.infer<typeof manifestSchema>;
type Configuration = { baseUrl: string; channel: string };
type Installation = { platform: string; nativeBuild: string };

type UpdateOptions = { app: App } & (
  { mode: 'background' } | { mode: 'required'; minAppVersion: string }
);

type UpdateOutcome =
  | { status: 'no-update' }
  | { status: 'staged'; bundleId: string }
  | { status: 'reloading'; bundleId: string }
  | { status: 'store-required' };

type ActiveUpdate = {
  requestKey: string;
  finished: Promise<void>;
  outcome: UpdateOutcome;
  failed: boolean;
  error?: unknown;
};

let activeUpdate: ActiveUpdate | undefined;

export async function updateApp(options: UpdateOptions): Promise<UpdateOutcome> {
  const { app, mode } = options;
  const minimum = options.mode === 'required' ? options.minAppVersion : undefined;
  const requestKey = JSON.stringify([app, mode, minimum]);

  // Identical requests share the outcome; different requirements wait for their own check.
  while (activeUpdate) {
    const previous = activeUpdate;
    await previous.finished;

    if (previous.requestKey !== requestKey) {
      continue;
    }

    if (previous.failed) {
      throw previous.error;
    }

    return previous.outcome;
  }

  let finish!: () => void;
  const operation: ActiveUpdate = {
    requestKey,
    finished: new Promise<void>((resolve) => {
      finish = resolve;
    }),
    outcome: mode === 'required' ? { status: 'store-required' } : { status: 'no-update' },
    failed: false,
  };
  activeUpdate = operation;

  try {
    if (!Capacitor.isNativePlatform()) return operation.outcome;
    if (minimum !== undefined && !isValidAppVersion(minimum)) {
      throw new Error('Invalid minimum app version.');
    }

    const config = readConfiguration();
    if (!config) return operation.outcome;

    const installation = await readInstallation();
    const manifestUrl = buildManifestUrl(config, installation, app);
    const response = await fetchManifest(manifestUrl);
    if (!response.found) return operation.outcome;

    const manifest = validateManifest(response.body, config, installation, app);
    if (!manifest.compatibleNativeBuilds.includes(installation.nativeBuild)) {
      return operation.outcome;
    }
    if (
      minimum !== undefined &&
      (!manifest.appVersion || compareAppVersions(manifest.appVersion, minimum) < 0)
    ) {
      return operation.outcome;
    }

    const bundles = await readBundleState();
    if (bundles.current === manifest.bundleId || bundles.blocked.includes(manifest.bundleId)) {
      console.info('[OTA] Skipping active or blocked bundle.');
      return operation.outcome;
    }

    if (bundles.pending !== manifest.bundleId) {
      if (!bundles.downloaded.includes(manifest.bundleId)) {
        await downloadBundle(manifest);
      }
      await stageBundle(manifest.bundleId);
    }

    if (mode === 'required') {
      await activateBundle();
      operation.outcome = { status: 'reloading', bundleId: manifest.bundleId };
    } else {
      operation.outcome = { status: 'staged', bundleId: manifest.bundleId };
    }
    return operation.outcome;
  } catch (error) {
    operation.failed = true;
    operation.error = error;
    throw error;
  } finally {
    activeUpdate = undefined;
    finish();
  }
}

function readConfiguration(): Configuration | null {
  const baseUrl = import.meta.env.VITE_OTA_BASE_URL;
  const channel = import.meta.env.VITE_OTA_CHANNEL;
  if (!baseUrl || !channel) return null;
  return { baseUrl: baseUrl.replace(/\/$/, ''), channel };
}

async function readInstallation(): Promise<Installation> {
  const { versionCode } = await LiveUpdate.getVersionCode();
  return { platform: Capacitor.getPlatform(), nativeBuild: versionCode };
}

function buildManifestUrl(config: Configuration, installation: Installation, app: App): string {
  return (
    `${config.baseUrl}/${app}/${installation.platform}/` +
    `native-build-${encodeURIComponent(installation.nativeBuild)}/` +
    `${encodeURIComponent(config.channel)}.json`
  );
}

async function fetchManifest(
  url: string
): Promise<{ found: false } | { found: true; body: unknown }> {
  console.info('[OTA] Checking:', url);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(url, { cache: 'no-cache', signal: controller.signal });
    if (response.status === 404) {
      console.info('[OTA] No release published for this build.');
      return { found: false };
    }
    if (!response.ok) {
      throw new Error(`Manifest request failed: ${response.status}`);
    }
    return { found: true, body: await response.json() };
  } finally {
    clearTimeout(timeout);
  }
}

function validateManifest(
  body: unknown,
  config: Configuration,
  installation: Installation,
  app: App
): Manifest {
  const manifest = manifestSchema.parse(body);
  if (manifest.app !== app || manifest.platform !== installation.platform) {
    throw new Error('Manifest does not match this installation.');
  }
  const downloadUrl = new URL(manifest.downloadUrl);
  const releaseDirectory = new URL(`${config.baseUrl}/${app}/releases/`);
  if (
    downloadUrl.protocol !== 'https:' ||
    downloadUrl.origin !== releaseDirectory.origin ||
    !downloadUrl.pathname.startsWith(releaseDirectory.pathname)
  ) {
    throw new Error('Bundle URL must use this app’s HTTPS release directory.');
  }
  return manifest;
}

async function readBundleState() {
  const [current, pending, blocked, downloaded] = await Promise.all([
    LiveUpdate.getCurrentBundle(),
    LiveUpdate.getNextBundle(),
    LiveUpdate.getBlockedBundles(),
    LiveUpdate.getDownloadedBundles(),
  ]);
  return {
    current: current.bundleId,
    pending: pending.bundleId,
    blocked: blocked.bundleIds,
    downloaded: downloaded.bundleIds,
  };
}

async function downloadBundle(manifest: Manifest): Promise<void> {
  console.info('[OTA] Downloading:', manifest.bundleId);
  await LiveUpdate.downloadBundle({
    artifactType: 'zip',
    bundleId: manifest.bundleId,
    url: manifest.downloadUrl,
    checksum: manifest.sha256,
    signature: manifest.signature,
  });
}

async function stageBundle(bundleId: string): Promise<void> {
  await LiveUpdate.setNextBundle({ bundleId });
  console.info('[OTA] Update staged for next launch:', bundleId);
}

async function activateBundle(): Promise<void> {
  await LiveUpdate.reload();
}
